import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Typography, Box, Chip, Stack, CircularProgress,
    Button, Grid, Paper, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetEmployee } from '../../hooks/employees/useGetEmployee';
import { useTranslation } from 'react-i18next';

const ModelShowEmployee = ({ open, onClose, employeeId }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { employee: empData, isLoading } = useGetEmployee(employeeId);
    const emp = empData?.data;

    const fmt = (v) => v || '-';
    const fmtDate = (v) => v ? new Date(v).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US') : '-';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
            PaperProps={{ sx: { borderRadius: 3, bgcolor: theme.palette.background.card } }}>
            <DialogTitle sx={{ m: 0, px: 3, py: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{isArabic ? 'تفاصيل الموظف' : 'Employee Details'}</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2, pb: 3, px: 3, direction: isArabic ? 'rtl' : 'ltr' }}>
                {isLoading || !employeeId ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>
                ) : !emp ? (
                    <Typography align="center" sx={{ py: 4 }}>{isArabic ? 'لا توجد بيانات' : 'No data'}</Typography>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: theme.palette.divider }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="h6" fontWeight={600}>{emp.full_name}</Typography></Grid>

                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الكود' : 'Code'} value={fmt(emp.code)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الجنس' : 'Gender'} value={emp.gender === 'male' ? (isArabic ? 'ذكر' : 'Male') : (isArabic ? 'أنثى' : 'Female')} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الهاتف' : 'Phone'} value={fmt(emp.phone)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'رقم البطاقة' : 'National ID'} value={fmt(emp.national_id)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'تاريخ الميلاد' : 'Date of Birth'} value={fmtDate(emp.date_of_birth)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'العمر' : 'Age'} value={emp.age != null ? emp.age : '-'} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'تاريخ التعيين' : 'Hire Date'} value={fmtDate(emp.hire_date)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الراتب' : 'Salary'} value={emp.salary != null ? emp.salary : '-'} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'العنوان' : 'Address'} value={fmt(emp.address)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'المدرسة' : 'School'} value={fmt(emp.school_name)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'القسم' : 'Department'} value={fmt(emp.department_name)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'المسمى' : 'Job Title'} value={fmt(emp.job_title_name)} /></Grid>

                            <Grid item xs={12}>
                                <Chip
                                    label={emp.is_active ? (isArabic ? 'مفعّل' : 'Active') : (isArabic ? 'موقوف' : 'Inactive')}
                                    size="small" color={emp.is_active ? 'success' : 'error'} variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                <Button onClick={onClose} variant="contained">{isArabic ? 'إغلاق' : 'Close'}</Button>
            </DialogActions>
        </Dialog>
    );
};

const Field = ({ label, value }) => (
    <>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </>
);

export default ModelShowEmployee;
